// src/components/BotEditPage.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';
import { ArrowBack, Delete, Save, Terminal, Refresh, ExpandMore } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import BotManagersPanel from './BotManagersPanel';
import apiFetch from '../utils/api';

function BotEditPage() {
  const { namespace, botName, id } = useParams();
  const navigate = useNavigate();

  // Support both new namespace/botName routes and legacy :id route
  const isLegacyRoute = !namespace && id;
  const apiBase = isLegacyRoute ? `/api/bots/${id}` : `/api/bots/${namespace}/${botName}`;

  const [bot, setBot] = useState(null);
  const [permission, setPermission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Form fields
  const [name, setName] = useState('');
  const [prefix, setPrefix] = useState('');
  const [modmailCategoryId, setModmailCategoryId] = useState('');
  const [logChannelId, setLogChannelId] = useState('');
  const [inboxServerId, setInboxServerId] = useState('');
  const [mainServerId, setMainServerId] = useState('');
  const [status, setStatus_] = useState('');
  const [statusType, setStatusType] = useState('playing');
  const [responseMessage, setResponseMessage] = useState('');
  const [closeMessage, setCloseMessage] = useState('');
  const [allowUserClose, setAllowUserClose] = useState('');
  const [mentionRole, setMentionRole] = useState('');
  const [typingProxy, setTypingProxy] = useState('');
  const [enableGreeting, setEnableGreeting] = useState('');
  const [greetingMessage, setGreetingMessage] = useState('');

  // Logs state
  const [logsExpanded, setLogsExpanded] = useState(false);
  const [logs, setLogs] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [logLines, setLogLines] = useState(100);
  const logsEndRef = React.useRef(null);

  useEffect(() => {
    fetchBot();
  }, [namespace, botName, id]);

  const fetchBot = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(apiBase, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setBot(data);
        setPermission(data.permission || 'owner');
        setName(data.name);
        setPrefix(data.config?.prefix || '!');
        setModmailCategoryId(data.config?.modmailCategoryId || '');
        setLogChannelId(data.config?.logChannelId || '');
        setInboxServerId(data.config?.inboxServerId || '');
        setMainServerId(data.config?.mainServerId || '');
        setStatus_(data.config?.status || '');
        setStatusType(data.config?.statusType || 'playing');
        setResponseMessage(data.config?.responseMessage || '');
        setCloseMessage(data.config?.closeMessage || '');
        setAllowUserClose(data.config?.allowUserClose || '');
        setMentionRole(data.config?.mentionRole || '');
        setTypingProxy(data.config?.typingProxy || '');
        setEnableGreeting(data.config?.enableGreeting || '');
        setGreetingMessage(data.config?.greetingMessage || '');
      } else if (response.status === 404) {
        setError('Bot not found');
      } else {
        throw new Error('Failed to fetch bot');
      }
    } catch (err) {
      console.error('Error fetching bot:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async (lines) => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const response = await apiFetch(`${apiBase}/logs?lines=${lines || logLines}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || '');
      } else {
        throw new Error('Failed to fetch logs');
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      setLogsError(err.message);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (logsExpanded) {
      fetchLogs();
    }
  }, [logsExpanded]);

  useEffect(() => {
    if (logsExpanded && logsEndRef.current) {
      logsEndRef.current.scrollTop = logsEndRef.current.scrollHeight;
    }
  }, [logs]);

  const handleLogLinesChange = (e) => {
    const value = e.target.value;
    setLogLines(value);
    if (logsExpanded) {
      fetchLogs(value);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await apiFetch(apiBase, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          config: {
            prefix,
            modmailCategoryId: modmailCategoryId.trim(),
            logChannelId: logChannelId.trim(),
            inboxServerId: inboxServerId.trim(),
            mainServerId: mainServerId.trim(),
            status: status.trim(),
            statusType,
            responseMessage: responseMessage.trim(),
            closeMessage: closeMessage.trim(),
            allowUserClose,
            mentionRole: mentionRole.trim(),
            typingProxy,
            enableGreeting,
            greetingMessage: greetingMessage.trim(),
          },
        }),
      });

      if (response.ok) {
        const updatedBot = await response.json();
        setBot(updatedBot);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update bot');
      }
    } catch (err) {
      console.error('Error updating bot:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);

    try {
      const response = await apiFetch(apiBase, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        navigate('/bots');
      } else {
        throw new Error('Failed to delete bot');
      }
    } catch (err) {
      console.error('Error deleting bot:', err);
      setError(err.message);
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return 'success';
      case 'stopped':
        return 'default';
      case 'pending':
        return 'info';
      case 'creating':
        return 'info';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const isOwner = permission === 'owner';
  const canEdit = isOwner || permission === 'manager';
  const readOnly = !canEdit;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !bot) {
    return (
      <Box>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/bots')} sx={{ mt: 2 }}>
          Back to Bots
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <IconButton onClick={() => navigate('/bots')} sx={{ mt: 0.5, bgcolor: 'rgba(255,255,255,0.04)' }}>
              <ArrowBack />
            </IconButton>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Typography variant="h4" component="h1">
                  {bot?.name || 'Edit Bot'}
                </Typography>
                {bot && <Chip label={bot.status} color={getStatusColor(bot.status)} size="small" />}
                {!isOwner && permission && (
                  <Chip label={permission} variant="outlined" size="small" />
                )}
              </Box>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Configure your bot's settings, messages, and behavior.
              </Typography>
            </Box>
          </Box>
          {isOwner && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => setDeleteDialogOpen(true)}
              size="small"
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Bot updated successfully!
        </Alert>
      )}

      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have {permission} access to this bot. Editing is restricted.
        </Alert>
      )}

      <Card>
        <CardContent>
          <Grid container spacing={3}>
            {/* General section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: -1 }}>
                General
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bot Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                variant="outlined"
                required
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Command Prefix"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                variant="outlined"
                helperText="The prefix for bot commands (e.g., !, ?, $)"
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Modmail Category ID"
                value={modmailCategoryId}
                onChange={(e) => setModmailCategoryId(e.target.value)}
                variant="outlined"
                helperText="Discord category ID for modmail channels"
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Log Channel ID"
                value={logChannelId}
                onChange={(e) => setLogChannelId(e.target.value)}
                variant="outlined"
                helperText="Discord channel ID for bot logs"
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Inbox Server ID"
                value={inboxServerId}
                onChange={(e) => setInboxServerId(e.target.value)}
                variant="outlined"
                helperText="Discord server ID where modmail channels are created"
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Main Server ID"
                value={mainServerId}
                onChange={(e) => setMainServerId(e.target.value)}
                variant="outlined"
                helperText="Discord server ID of your main community server"
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mention Role"
                value={mentionRole}
                onChange={(e) => setMentionRole(e.target.value)}
                variant="outlined"
                helperText="Role ID to mention on new threads (leave blank for none)"
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: -1 }}>
                Status & Messages
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined" disabled={readOnly}>
                <InputLabel>Status Type</InputLabel>
                <Select
                  value={statusType}
                  onChange={(e) => setStatusType(e.target.value)}
                  label="Status Type"
                >
                  <MenuItem value="playing">Playing</MenuItem>
                  <MenuItem value="watching">Watching</MenuItem>
                  <MenuItem value="listening">Listening</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Status Text"
                value={status}
                onChange={(e) => setStatus_(e.target.value)}
                variant="outlined"
                helperText="Bot's Discord status message"
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Auto-Response Message"
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                variant="outlined"
                multiline
                rows={2}
                helperText="Message sent to user when a new thread is created"
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Close Message"
                value={closeMessage}
                onChange={(e) => setCloseMessage(e.target.value)}
                variant="outlined"
                multiline
                rows={2}
                helperText="Message sent to user when a thread is closed"
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Greeting Message"
                value={greetingMessage}
                onChange={(e) => setGreetingMessage(e.target.value)}
                variant="outlined"
                multiline
                rows={2}
                helperText="Message sent when a new member joins (requires Enable Greeting)"
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: -1 }}>
                Behavior
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined" disabled={readOnly}>
                <InputLabel>Allow User Close</InputLabel>
                <Select
                  value={allowUserClose}
                  onChange={(e) => setAllowUserClose(e.target.value)}
                  label="Allow User Close"
                >
                  <MenuItem value="">Default</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined" disabled={readOnly}>
                <InputLabel>Typing Proxy</InputLabel>
                <Select
                  value={typingProxy}
                  onChange={(e) => setTypingProxy(e.target.value)}
                  label="Typing Proxy"
                >
                  <MenuItem value="">Default</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined" disabled={readOnly}>
                <InputLabel>Enable Greeting</InputLabel>
                <Select
                  value={enableGreeting}
                  onChange={(e) => setEnableGreeting(e.target.value)}
                  label="Enable Greeting"
                >
                  <MenuItem value="">Default</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {canEdit && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button variant="outlined" onClick={() => navigate('/bots')}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                    onClick={handleSave}
                    disabled={saving || !name.trim()}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Bot Managers Panel - only visible to owners */}
      {!isLegacyRoute && (
        <BotManagersPanel
          namespace={namespace}
          botName={botName}
          isOwner={isOwner}
        />
      )}

      {/* Container Logs Panel */}
      {!isLegacyRoute && (
        <Accordion
          expanded={logsExpanded}
          onChange={(_, expanded) => setLogsExpanded(expanded)}
          sx={{ mt: 3 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Terminal />
              <Typography variant="h6">Container Logs</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Lines</InputLabel>
                <Select value={logLines} label="Lines" onChange={handleLogLinesChange}>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={250}>250</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                onClick={() => fetchLogs()}
                disabled={logsLoading}
              >
                Refresh
              </Button>
              {logsLoading && <CircularProgress size={20} />}
            </Box>
            {logsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {logsError}
              </Alert>
            )}
            <Box
              ref={logsEndRef}
              component="pre"
              sx={{
                bgcolor: '#0d1117',
                color: '#7ee787',
                fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                fontSize: '12px',
                p: 2,
                borderRadius: 1,
                maxHeight: 500,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                m: 0,
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              {logs || 'No logs available.'}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Bot?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{bot?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BotEditPage;
